// src/components/block/liveStreamManagment/autoDirector.ts
// v1.0 — Moteur de décision du « Réalisateur auto » de la régie.
//
// Ce fichier ne touche ni à React ni au DOM : il reçoit des niveaux et rend une
// décision. C'est ce qui permet d'ajouter d'autres mécanismes de choix (rotation,
// priorité à un invité, etc.) sans rien changer au câblage : un mécanisme est une
// entrée de AUTO_DIRECTOR_MODES et une branche de `AutoDirectorEngine.choose`.
//
// Mécanisme livré : « loudest » — la source dont le niveau sonore lissé est le
// plus élevé passe en final. Trois protections évitent l'effet mitrailleuse :
//   • un seuil de silence : sous ce niveau, une source ne peut pas prendre l'antenne ;
//   • une marge : le challenger doit dépasser la source à l'antenne d'un écart net ;
//   • deux durées : il doit tenir cette avance `reactionMs`, et une source ne
//     quitte pas l'antenne avant `minHoldMs`.

export type AutoDirectorMode = 'loudest';

export interface AutoDirectorModeInfo {
  id          : AutoDirectorMode;
  label       : string;
  description : string;
}

export const AUTO_DIRECTOR_MODES: AutoDirectorModeInfo[] = [
  {
    id: 'loudest',
    label: 'Niveau sonore le plus fort',
    description: "La source qui parle (ou fait) le plus de bruit passe en final.",
  },
];

export interface AutoDirectorSettings {
  mode                : AutoDirectorMode;
  /** Sources admises. null = toutes les sources du deck. */
  candidateIds        : string[] | null;
  /** Niveau (échelle 0–100 des VU-mètres) sous lequel une source est tenue pour silencieuse. */
  silenceThresholdPct : number;
  /** Avance minimale, en points d'échelle, qu'un challenger doit avoir sur la source à l'antenne. */
  switchMarginPct     : number;
  /** Durée pendant laquelle le challenger doit rester devant avant la bascule. */
  reactionMs          : number;
  /** Durée minimale d'une source à l'antenne avant qu'une autre puisse la remplacer. */
  minHoldMs           : number;
  /** Pause du réalisateur après un choix manuel de l'opérateur. 0 = aucune pause. */
  manualPauseMs       : number;
}

export const DEFAULT_AUTO_DIRECTOR_SETTINGS: AutoDirectorSettings = {
  mode                : 'loudest',
  candidateIds        : null,
  silenceThresholdPct : 35,
  switchMarginPct     : 8,
  reactionMs          : 700,
  minHoldMs           : 3000,
  manualPauseMs       : 10000,
};

/** Bornes des réglages — sert à la fois à l'interface et à la relecture du stockage. */
export const AUTO_DIRECTOR_LIMITS = {
  silenceThresholdPct : { min: 0,   max: 100,   step: 1   },
  switchMarginPct     : { min: 0,   max: 50,    step: 1   },
  reactionMs          : { min: 0,   max: 5000,  step: 100 },
  minHoldMs           : { min: 0,   max: 30000, step: 500 },
  manualPauseMs       : { min: 0,   max: 120000, step: 1000 },
} as const;

const clamp = (v: unknown, min: number, max: number, fallback: number): number => {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  return Math.min(max, Math.max(min, n));
};

/** Relit des réglages venus du stockage : toute valeur absente ou hors bornes retombe sur le défaut. */
export function sanitizeAutoDirectorSettings(raw: unknown): AutoDirectorSettings {
  const d = DEFAULT_AUTO_DIRECTOR_SETTINGS;
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<AutoDirectorSettings>;
  const L = AUTO_DIRECTOR_LIMITS;
  return {
    mode: AUTO_DIRECTOR_MODES.some(m => m.id === r.mode) ? (r.mode as AutoDirectorMode) : d.mode,
    candidateIds: Array.isArray(r.candidateIds)
      ? r.candidateIds.filter((x): x is string => typeof x === 'string')
      : null,
    silenceThresholdPct: clamp(r.silenceThresholdPct, L.silenceThresholdPct.min, L.silenceThresholdPct.max, d.silenceThresholdPct),
    switchMarginPct    : clamp(r.switchMarginPct,     L.switchMarginPct.min,     L.switchMarginPct.max,     d.switchMarginPct),
    reactionMs         : clamp(r.reactionMs,          L.reactionMs.min,          L.reactionMs.max,          d.reactionMs),
    minHoldMs          : clamp(r.minHoldMs,           L.minHoldMs.min,           L.minHoldMs.max,           d.minHoldMs),
    manualPauseMs      : clamp(r.manualPauseMs,       L.manualPauseMs.min,       L.manualPauseMs.max,       d.manualPauseMs),
  };
}

// ── Moteur ────────────────────────────────────────────────────────────────────

/** Constante de temps du lissage : un pic bref (toux, claquement) ne suffit pas à voler l'antenne. */
const SMOOTHING_TAU_MS = 400;

export interface AutoDirectorTick {
  /** Horodatage courant, en ms. */
  now       : number;
  /** Niveau instantané (0–100) de chaque source candidate. Une source absente vaut 0. */
  levels    : ReadonlyMap<string, number>;
  /** Source actuellement au final ('' ou null si aucune). */
  currentId : string | null;
}

export class AutoDirectorEngine {
  private smoothed      = new Map<string, number>();
  private lastTickAt    = 0;
  private challengerId  : string | null = null;
  private challengerSince = 0;
  private lastCurrentId : string | null = null;
  private currentSince  = 0;
  private pausedUntil   = 0;

  /** Oublie tout l'état : à appeler quand le réalisateur s'enclenche ou change de chaîne. */
  reset(): void {
    this.smoothed.clear();
    this.lastTickAt = 0;
    this.challengerId = null;
    this.challengerSince = 0;
    this.lastCurrentId = null;
    this.currentSince = 0;
    this.pausedUntil = 0;
  }

  /** Suspend les décisions jusqu'à `until` (ms). Le lissage continue de tourner. */
  pauseUntil(until: number): void {
    this.pausedUntil = Math.max(this.pausedUntil, until);
    this.challengerId = null;
  }

  isPaused(now: number): boolean {
    return now < this.pausedUntil;
  }

  /** Niveau lissé d'une source — exposé pour le débogage et les tests. */
  getSmoothed(id: string): number {
    return this.smoothed.get(id) ?? 0;
  }

  /**
   * Un pas de décision. Renvoie l'id de la source à mettre au final, ou null si
   * rien ne doit changer. L'appelant applique la bascule et fournit la valeur de
   * `currentId` au pas suivant : le moteur ne suppose jamais qu'elle a réussi.
   */
  tick(settings: AutoDirectorSettings, candidateIds: readonly string[], t: AutoDirectorTick): string | null {
    const { now, levels, currentId } = t;

    // Lissage exponentiel indexé sur le temps réel écoulé, pas sur le nombre de pas :
    // un onglet ralenti par le navigateur ne change donc pas la réactivité.
    const dt    = this.lastTickAt === 0 ? SMOOTHING_TAU_MS : Math.max(0, now - this.lastTickAt);
    const alpha = 1 - Math.exp(-dt / SMOOTHING_TAU_MS);
    this.lastTickAt = now;
    for (const id of candidateIds) {
      const prev = this.smoothed.get(id) ?? 0;
      this.smoothed.set(id, prev + alpha * ((levels.get(id) ?? 0) - prev));
    }
    for (const id of Array.from(this.smoothed.keys())) {
      if (!candidateIds.includes(id)) this.smoothed.delete(id);
    }

    // Qu'elle vienne de nous, de l'opérateur ou d'ailleurs, toute arrivée d'une
    // nouvelle source au final relance le compteur de tenue.
    if (currentId !== this.lastCurrentId) {
      this.lastCurrentId = currentId;
      this.currentSince = now;
      this.challengerId = null;
    }

    if (this.isPaused(now)) return null;
    return this.choose(settings, candidateIds, now, currentId);
  }

  private choose(settings: AutoDirectorSettings, candidateIds: readonly string[], now: number, currentId: string | null): string | null {
    switch (settings.mode) {
      case 'loudest':
      default:
        return this.chooseLoudest(settings, candidateIds, now, currentId);
    }
  }

  private chooseLoudest(settings: AutoDirectorSettings, candidateIds: readonly string[], now: number, currentId: string | null): string | null {
    let best: string | null = null;
    let bestLevel = -1;
    for (const id of candidateIds) {
      const lv = this.smoothed.get(id) ?? 0;
      if (lv > bestLevel) { best = id; bestLevel = lv; }
    }

    // Personne ne dépasse le seuil de silence, ou le meilleur EST déjà à l'antenne :
    // on garde ce qu'on a, sans chercher de remplaçant.
    if (best === null || bestLevel < settings.silenceThresholdPct || best === currentId) {
      this.challengerId = null;
      return null;
    }

    // Une source à l'antenne qui n'est pas (ou plus) candidate ne se défend pas :
    // le challenger la remplace dès qu'il a tenu sa durée de réaction.
    const currentIsCandidate = currentId !== null && candidateIds.includes(currentId);
    if (currentIsCandidate) {
      const currentLevel = this.smoothed.get(currentId as string) ?? 0;
      if (bestLevel < currentLevel + settings.switchMarginPct) {
        this.challengerId = null;
        return null;
      }
      if (now - this.currentSince < settings.minHoldMs) return null;
    }

    if (this.challengerId !== best) {
      this.challengerId = best;
      this.challengerSince = now;
    }
    if (now - this.challengerSince < settings.reactionMs) return null;

    this.challengerId = null;
    return best;
  }
}
